import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { checkAccess } from '../core/utils';
import { setCurrentModule } from '../redux/reducers/utils/navReducer';
import { useModerationProductsCount } from '../hooks/useFetchModerationCount';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as IconModeration } from '../assets/icons/icon-moderation.svg';

const SiderMenuComponent = ({ modules, onClose, $invert }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isMobile } = useSelector((store) => store.common);
  const { currentModule } = useSelector((store) => store.nav);
  const { accessComponentsForUser } = useSelector((store) => store.services);
  const totalProduct = useSelector((store) => store.moderationProducts.totalModerationProducts);
  const totalReviews = useSelector((store) => store.moderationReviews.total);
  useModerationProductsCount();

  // Состояние для мобильного раскрытия подменю
  const [openMobile, setOpenMobile] = useState(null);

  // Фильтрация доступных модулей
  const accessModules = modules?.map(module => {
    if (!checkAccess(accessComponentsForUser, module.key)) return null;

    // Для пункта "Модерация" добавляем бадж
    if (module.key === 'moderation') {
      return {
        ...module,
        icon: (totalProduct || totalReviews) > 0 ? (
              <div>
                {(totalProduct) > 0 && <CountProduct>{totalProduct}</CountProduct>}
                {(totalReviews) > 0 && <CountReview>{totalReviews}</CountReview>}
                <IconModeration/>
              </div>
            ) : (
              <IconModeration/>
            ),
      };
    }

    return module;
  }).filter(Boolean);
  
  // Обработка клика по основному пункту меню
  const handleMainClick = (key) => {
    dispatch(setCurrentModule(key));
    if (onClose) onClose();
  };

  // Проверка активного пункта
  const isActive = (key, to) => {
    return location.pathname === to || currentModule === key;
  };

  return (
    <MenuWrapper $invert={$invert}>
      {accessModules.map(module => {
        const accessPages = Array.isArray(module.pages)
          ? module.pages.filter(page => checkAccess(accessComponentsForUser, page.key))
          : [];
        const hasPages = accessPages.length > 0;
        const isOpen = openMobile === module.key;
        const labelText = module.label?.props?.children;
        return (
          <MenuItemWrapper key={module.key}>
            {isMobile && hasPages ? (
              <MenuItemMobileLabel
                onClick={() => setOpenMobile(isOpen ? null : module.key)}
                $active={isOpen}
              >
                <IconBox>{module.icon}</IconBox>
                <span style={{ flex: 1 }}>{labelText}</span>
                <Arrow $open={isOpen}>▼</Arrow>
              </MenuItemMobileLabel>
            ) : (
              <MenuItemButton
                as={Link}
                to={module.label.props.to}
                onClick={() => handleMainClick(module.key)}
                $active={isActive(module.key, module.label.props.to)}
              >
                <IconBox>{module.icon}</IconBox>
                {isMobile && <span style={{ flex: 1 }}>{labelText}</span>}
              </MenuItemButton>
            )}
            {hasPages && (
              <SubMenuWrapper
                $columns={accessPages.length > 6 ? 2 : 1}
                $mobile={isMobile}
                $expanded={isMobile ? isOpen : false}
              >
                {accessPages.map(page => (
                  <SubMenuItem
                    as={Link}
                    to={page.label.props.to}
                    key={page.key}
                    $active={isActive(page.key, page.label.props.to)}
                    onClick={onClose}
                  >
                    {page.label.props.children}
                  </SubMenuItem>
                ))}
              </SubMenuWrapper>
            )}
          </MenuItemWrapper>
        );
      })}
    </MenuWrapper>
  );
};

export default SiderMenuComponent;

const Count = styled.button`
    position: absolute;
    top: -8px;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 20px;
    height: 20px;
    padding: 0 4px;
    background-color: #2A2A2A;
    border-radius: 24px;
    color: #ffffff;
    font-size: 12px;
    border: none;
    z-index: 2;
    box-shadow: 0 0 0 0 rgba(234, 57, 75, 0.2);
    overflow: visible;
    &::before, &::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: rgba(234, 57, 75, 0.25);
        z-index: 1;
        pointer-events: none;
        animation: ripple 1.6s infinite;
    }
    &::after {
        animation-delay: 0.8s;
    }
    
    @keyframes ripple {
        0% {
            opacity: 0.7;
            transform: translate(-50%, -50%) scale(1);
        }
        70% {
            opacity: 0.2;
            transform: translate(-50%, -50%) scale(2);
        }
        100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(4);
        }
    }
`;

const CountProduct = styled(Count)`
    right: 12px;
    background-color: #EA394B;
    animation: blinkRed 1.2s infinite;
    @keyframes blinkRed {
        0% { background-color: #2A2A2A; box-shadow: none; }
        50% { background-color: #EA394B; box-shadow: 0px 0px 10px rgba(234, 57, 75, 0.5); }
        100% { background-color: #2A2A2A; box-shadow: none; }
    }
`;

const CountReview = styled(Count)`
    right: -12px;
    background-color: #3965ea;
    animation: blinkBlue 1.2s infinite;
    @keyframes blinkBlue {
        0% { background-color: #2A2A2A; box-shadow: none; }
        50% { background-color: #3965ea; box-shadow: 0px 0px 10px rgba(57, 101, 234, 0.5); }
        100% { background-color: #2A2A2A; box-shadow: none; }
    }
`;

// Стилизация
const MenuWrapper = styled.nav`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  z-index: 900;
    filter: ${(p) => p.$invert ? 'invert(1)' : 'none'};
`;

const MenuItemWrapper = styled.div`
  position: relative;
  &:hover > div {
    display: block;
  }
  @media (max-width: 600px) {
    margin-bottom: 12px;
    &:hover > div {
      display: none;
    }
  }
`;

const MenuItemButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: ${({ $active }) => ($active ? '#ff1100' : '#222')};
  background: ${({ $active }) => ($active ? '#ffe6e4' : 'transparent')};
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  border: none;
  outline: none;
  &:hover {
    background: #ffe6e4;
    color: #ff1100;
  }
  span {
    font-size: 15px;
    line-height: 1.2;
  }
`;

const IconBox = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SubMenuWrapper = styled.div`
  display: none;
  position: absolute;
  left: 100%;
  top: 0;
  min-width: 180px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  border-radius: 6px;
  z-index: 10;
  padding: 4px;
  ${({ $columns }) => $columns > 1 && `
    min-width: 360px;
    column-count: 2;
    column-gap: 4px;
    max-width: 400px;
  `}
  ${({ $mobile, $expanded }) => $mobile && `
    display: block !important;
    position: static;
    min-width: 0;
    box-shadow: none;
    border-radius: 12px;
    background: #fff;
    margin-top: 8px;
    padding: ${$expanded ? '12px 0 12px 48px' : '0 0 0 48px'};
    max-height: ${$expanded ? '420px' : '0'};
    column-count: 1;
    overflow: hidden;
    opacity: ${$expanded ? '1' : '0'};
    transition: max-height 0.35s ease, opacity 0.25s ease, padding 0.35s ease;
    pointer-events: ${$expanded ? 'auto' : 'none'};
  `}
`;

const SubMenuItem = styled(Link)`
  display: block;
  padding: 10px 24px;
  color: ${({ $active }) => ($active ? '#ff1100' : '#222')};
  background: ${({ $active }) => ($active ? '#ffe6e4' : 'transparent')};
  border-radius: 4px;
  text-decoration: none;
  font-size: 15px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  border: none;
  outline: none;
  &:hover {
    background: #ffe6e4;
    color: #ff1100;
  }
  @media (max-width: 600px) {
    background: transparent;
    padding: 8px 0;
    border-radius: 0;
    white-space: normal;
  }
`;

const MenuItemMobileLabel = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  width: 100%;
  color: ${({ $active }) => ($active ? '#ff1100' : '#222')};
  background: ${({ $active }) => ($active ? '#ffe6e4' : '#fdfdfd')};
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s, color 0.2s;
  border: 1px solid rgba(0,0,0,0.06);
  outline: none;
  span {
    font-size: 15px;
    line-height: 1.2;
    text-align: left;
  }
  &:hover {
    background: #ffe6e4;
    color: #ff1100;
  }
  @media (min-width: 601px) {
    display: none;
  }
`;

const Arrow = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  font-size: 12px;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.25s ease;
  color: inherit;
`;
